import { z } from "zod";
import { UseMutateFunction } from "@tanstack/react-query";
import { Dialog } from "@shared/ui/Dialog";
import { ButtonProps } from "@shared/ui/Button";
import { useDialog } from "@shared/features/dialog/useDialog";
import { Form, FieldType } from "./Form";
import { useForm } from "./useForm";

type FormDialogProps = {
  onSubmit: UseMutateFunction<any, Error, any, unknown>;
  onClose?: () => void;
  fields: FieldType[];
  title: string;
  description: string;
  defaultValues?: any;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  primaryButtonProps?: ButtonProps;
  successTitle?: string;
  successDescription?: string;
  onSecondaryClick?: () => void;
  dialogName: string;
  schema: z.ZodSchema;
};

export function FormDialog({
  dialogName,
  onClose,
  defaultValues,
  onSubmit,
  schema,
  onSecondaryClick,
  fields,
  title,
  description,
  primaryButtonText = "Submit",
  primaryButtonProps,
  secondaryButtonText = "Cancel",
  successTitle = "Success",
  successDescription,
}: FormDialogProps) {
  const { activeDialog, closeDialog } = useDialog();

  const { form, handleSubmit, handleClose } = useForm({
    defaultValues: defaultValues,
    onSubmit,
    onClose: () => {
      closeDialog(dialogName);
      onClose?.();
    },
    schema,
  });

  const isOpen = activeDialog === dialogName;

  const {
    formState: { isSubmitSuccessful, isSubmitting, isLoading, isValid },
  } = form;

  return (
    <Dialog
      isOpen={isOpen}
      isWithActions={!isSubmitSuccessful}
      onClose={handleClose}
      title={isSubmitSuccessful ? successTitle : title}
      description={isSubmitSuccessful ? successDescription : description}
      primaryButtonText={primaryButtonText}
      onPrimaryClick={handleSubmit}
      onSecondaryClick={onSecondaryClick || handleClose}
      secondaryButtonText={secondaryButtonText}
      primaryButtonProps={{
        disabled: isSubmitting || isLoading || !isValid,
        type: "submit",
        ...primaryButtonProps,
      }}
      secondaryButtonProps={{
        disabled: isSubmitting || isLoading,
      }}
    >
      {!isSubmitSuccessful && (
        <Form
          className="py-8"
          form={form}
          fields={fields as FieldType[]}
          errorContainerClassName="p-0 mt-8"
        />
      )}
    </Dialog>
  );
}

export default FormDialog;
